/**
 * A-GTCC-VIEW-PSET-DZ1 — read client for the view "Platform Settings" (`pset`),
 * reference `docs/design/GTCC_REFERENCE_2026-09-01.html:849-869`.
 *
 * Three panels, three existing sources plus one new read-only route:
 *
 *   - "Feature-Flags"      `GET /v1/feature-flags`
 *     (`services/api/src/modules/feature-flags/routes.ts:62`) — the same route
 *     `views/settings/useGtccSettings.ts` already reads. This module does NOT
 *     add a second flag source and adds no write path: toggling a flag stays
 *     where it is, in `GtccSettingsView`/`useGtccSettings`, with its audit.
 *   - "Plattform"          `GET /v1/admin/gtcc/pset/platform`
 *     (`services/api/src/modules/gtcc/pset/routes.ts`) — the session cap and
 *     the account deletion deadline, measured off the running configuration.
 *     The third row of the panel, the offered languages, is NOT fetched: the
 *     shipped `@genius-tracker/i18n` locale list IS that configuration, and
 *     routing it through the API would invent a second source for it.
 *   - "Aus der alten Konsole übernommen"  `GET /v1/admin/support-tickets`
 *     (`services/api/src/modules/support/…`) — only to count the open
 *     requests the reference prints. The two other rows of that panel are
 *     deep links and read nothing.
 *
 * Read-only by construction: `gtccGet` only, no mutation verb.
 */
import { gtccGet, type GtccResult } from "./gtcc-result.js";

/* ---------------------------------------------------------------- paths */

export const PSET_PLATFORM_PATH = "/v1/admin/gtcc/pset/platform";
export const PSET_FEATURE_FLAGS_PATH = "/v1/feature-flags";
/**
 * The inbox route caps `limit` at 100
 * (`services/api/src/modules/support/routes.ts:151`) and answers no total, so
 * the count this view can measure is capped at 100 as well. The view says so
 * instead of printing a number it did not measure: at the cap it reads
 * "mindestens 100", never a flat 100.
 */
export const PSET_SUPPORT_OPEN_LIMIT = 100;

/** Only open tickets are counted, and only their number is shown. */
export const PSET_SUPPORT_OPEN_PATH = `/v1/admin/support-tickets?status=open&limit=${PSET_SUPPORT_OPEN_LIMIT}`;

/* ----------------------------------------------------------- query keys */

export const PSET_PLATFORM_KEY = ["gtcc", "pset", "platform"] as const;
/**
 * Its own key, NOT the `["gtcc", "settings", "feature-flags"]` of
 * `useGtccSettings.useGtccFeatureFlags`. Same route, but not the same cached
 * value: that hook stores the raw response, while this read stores the
 * {@link GtccResult} envelope of `gtccGet`. Sharing the key would mean whichever
 * view mounts first decides which shape the other one finds — and the other one
 * then reads a payload it cannot parse. One key, one shape.
 */
export const PSET_FEATURE_FLAGS_KEY = ["gtcc", "pset", "feature-flags"] as const;
export const PSET_SUPPORT_OPEN_KEY = ["gtcc", "pset", "support-open"] as const;

/* ---------------------------------------------------------------- types */

/** `GET /v1/admin/gtcc/pset/platform` — mirrors `modules/gtcc/pset/dto.ts`. */
export interface PsetPlatformDto {
  readonly measuredAt: string;
  readonly sessionAbsoluteCapMinutes: number;
  readonly accountDeletionGraceDays: number;
}

/** A flag as `/v1/feature-flags` resolves it. */
export interface PsetFeatureFlagDto {
  readonly key: string;
  readonly wave: string;
  readonly visibility: "public_testable" | "internal_admin_only" | "hidden_disabled";
  readonly enabled: boolean;
  readonly visible: boolean;
  readonly source: "default" | "env";
  readonly description: string;
}

export interface PsetFeatureFlagsDto {
  readonly sprint: number;
  readonly resolvedAt: string;
  readonly flags: readonly PsetFeatureFlagDto[];
}

/** Only the field the count needs; the list itself is never rendered here. */
export interface PsetSupportTicketsDto {
  readonly items: readonly { readonly id: string }[];
}

/* --------------------------------------------------------------- reads */

export function fetchPsetPlatform(signal?: AbortSignal): Promise<GtccResult<PsetPlatformDto>> {
  return gtccGet<PsetPlatformDto>(PSET_PLATFORM_PATH, signal);
}

export function fetchPsetFeatureFlags(
  signal?: AbortSignal,
): Promise<GtccResult<PsetFeatureFlagsDto>> {
  return gtccGet<PsetFeatureFlagsDto>(PSET_FEATURE_FLAGS_PATH, signal);
}

export function fetchPsetOpenSupportTickets(
  signal?: AbortSignal,
): Promise<GtccResult<PsetSupportTicketsDto>> {
  return gtccGet<PsetSupportTicketsDto>(PSET_SUPPORT_OPEN_PATH, signal);
}

/* ----------------------------------------------------------- flag state */

/** The three states the reference spells out in the flag panel. */
export type PsetFlagState = "on" | "off" | "awaiting_key";

/**
 * Reference lines 853-856 print "AN", "wartet auf Schlüssel" and
 * "AUS (<Grund>)". The middle state is not a field of the flag table — it is
 * what a flag looks like that is switched off while its upstream key is
 * missing, and `/v1/feature-flags` says exactly that with
 * `enabled: false, source: "env"`: the environment decided it, not the
 * default. A flag off by its own default is plainly "AUS".
 *
 * Nothing here invents a reason: the reason shown next to "AUS" is the flag's
 * own `description`, unchanged.
 */
export function psetFlagState(flag: PsetFeatureFlagDto): PsetFlagState {
  if (flag.enabled) return "on";
  return flag.source === "env" ? "awaiting_key" : "off";
}
